import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../base/Button";
import { interviewService } from "../../services/interviewService";
import { cn } from "../../utils/cn";
import { useAuth } from "../../context/AuthContext";

interface HeaderProps {
    showAuth?: boolean;
}

function confirmLeavingActiveInterview(): boolean {
    return window.confirm("You haven't completed your interview. Are you sure you want to leave?");
}

async function cancelActiveInterviewIfAny() {
    const activeInterviewId = localStorage.getItem("activeInterviewId") || "";
    await interviewService.cancel_interview({ interview_id: activeInterviewId });
}

function UserAvatar({ imageUrl, name }: { imageUrl?: string | null; name?: string }) {
    return (
        <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-brand-100 ring-2 ring-brand-200">
            {imageUrl ? (
                <img src={imageUrl} alt={name || "User"} className="w-full h-full object-cover" />
            ) : (
                <i className="ri-user-line text-brand-600" />
            )}
        </div>
    );
}

export default function Header({ showAuth = true }: HeaderProps) {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    const handleLogoClick = async () => {
        const onUnfinishedVideoInterview =
            window.location.pathname.includes("/video-interview") &&
            localStorage.getItem("interviewCompleted") !== "true";

        if (onUnfinishedVideoInterview) {
            if (!confirmLeavingActiveInterview()) return;
            await cancelActiveInterviewIfAny();
        }

        navigate(user ? "/dashboard" : "/");
    };

    return (
        <header className="sticky top-0 z-40 w-full border-b border-gray-200 bg-white/80 backdrop-blur-xl">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between">
                    <button
                        onClick={handleLogoClick}
                        className="flex items-center gap-3 group transition-transform hover:scale-[1.02]"
                    >
                        <img
                            src="/logo.png"
                            alt="PitchMe.AI"
                            className="w-10 h-10 rounded-xl shadow-soft group-hover:shadow-medium transition-shadow"
                        />
                        <h1 className="text-xl font-bold text-gray-900">
                            PitchMe<span className="text-brand-600">.AI</span>
                        </h1>
                    </button>

                    {showAuth ? (
                        <div className="flex items-center gap-3">
                            <Button variant="ghost" size="sm" onClick={() => navigate("/login")}>
                                Sign In
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => navigate("/login")}
                                className="hidden sm:flex"
                            >
                                Get Started
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <button
                                    onClick={() => setMenuOpen((open) => !open)}
                                    className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-100 transition-colors"
                                >
                                    <UserAvatar imageUrl={user?.image_url} name={user?.name} />
                                    <span className="hidden sm:block text-sm font-medium text-gray-700">
                                        {user?.name}
                                    </span>
                                    <i
                                        className={cn(
                                            "ri-arrow-down-s-line text-gray-400 transition-transform",
                                            menuOpen && "rotate-180"
                                        )}
                                    />
                                </button>

                                {menuOpen && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-large border border-gray-200 py-2 animate-slide-down">
                                        <div className="px-4 py-2 border-b border-gray-100">
                                            <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setMenuOpen(false);
                                                navigate("/settings");
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                        >
                                            <i className="ri-settings-line" />
                                            Settings
                                        </button>
                                        <button
                                            onClick={() => {
                                                setMenuOpen(false);
                                                handleLogout();
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-error-600 hover:bg-error-50 flex items-center gap-2"
                                        >
                                            <i className="ri-logout-box-line" />
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
